// MedicineAutocomplete.tsx
import React, { useEffect, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useStyles } from "./styleUtils";
import HttpClient, { HttpGetOutput } from "../../lib/HttpClient";
import { parseMedicineInstructionField } from "./stateUtils";

/** Types */
export type Suggestion = {
  strength?: string;
  unit?: string;
  morning?: any;
  afternoon?: any;
  evening?: any;
  night?: any;
  frequencyPerWeek?: string;
  instruction?: string;
  type?: string;
  name?: string;
  search_name?: string;
  beforeFood?: any;
  category?: string;
  [k: string]: any;
};

type Props = {
  medicineName?: string;
  onSelectHandler: (value: Suggestion | null) => void;
  onNameChangeHandler: (value: string) => void;
  searchTemplates?: boolean;
};

/**
 * MedicineAutocomplete - repo-1 style using HttpClient
 */
const MedicineAutocomplete: React.FC<Props> = ({
  medicineName = "",
  onSelectHandler,
  onNameChangeHandler,
  searchTemplates = true,
}) => {
  const classes = useStyles();
  const [medSuggestions, setMedSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // helper: fetch templates (doctor templates) via HttpClient
  const searchMedicineTemplates = async (query: string) => {
    if (!query || query.trim() === "") return [];
    // NOTE: adapt this URL if your backend expects different path/params
    const url = `/api/v1/doctors/template/medicine/${encodeURIComponent(
      query.trim()
    )}`;
    try {
      const resp = await HttpClient.performGet(url);
      const body = (resp as HttpGetOutput<any>)?.body;
      return body?.medicineTemplate ?? [];
    } catch (err) {
      console.error("searchMedicineTemplates error:", err);
      return [];
    }
  };

  // helper: search global medicine DB via HttpClient
  const searchMedicine = async (query: string) => {
    if (!query || query.trim() === "") return [];
    const url = `/api/v1/medicine/${encodeURIComponent(
      query.trim()
    )}?chip=true`;
    try {
      const resp = await HttpClient.performGet(url);
      const body = (resp as HttpGetOutput<any>)?.body;
      return body?.medicines ?? [];
    } catch (err) {
      console.error("searchMedicine error:", err);
      return [];
    }
  };

  useEffect(() => {
    let mounted = true;

    if (!medicineName || medicineName.trim() === "") {
      setMedSuggestions([]);
      return;
    }

    (async () => {
      try {
        // run both queries in parallel; optionally skip templates
        const [templateResults, medResults] = await Promise.all([
          searchTemplates
            ? searchMedicineTemplates(medicineName)
            : Promise.resolve([]),
          searchMedicine(medicineName),
        ]);

        const medTempResultsProcessed: Suggestion[] = (
          templateResults ?? []
        ).flatMap((tpl: any) => {
          try {
            const m = tpl.medicines?.[0] ?? {};
            const parsedInstr = parseMedicineInstructionField(
              m.instruction ?? ""
            );
            return [
              {
                strength: String(m.strength ?? "").replace(/[^0-9./]/g, ""),
                unit: String(m.strength ?? "").replace(/[0-9./\s]/g, ""),
                morning: m.frequency?.[0] ?? 0,
                afternoon: m.frequency?.[1] ?? 0,
                night: m.frequency?.[2] ?? 0,
                evening: m.frequency?.[3] ?? 0,
                frequencyPerWeek: m.frequencyPerWeek ?? "Everyday",
                instruction: parsedInstr?.instructionText ?? "",
                type: m.drugType ?? "",
                name: m.drugName ?? "",
                search_name: `${tpl.templateName ?? ""} (Local)`,
                beforeFood: parsedInstr?.instructionDropdownOption ?? false,
              },
            ];
          } catch (e) {
            return [];
          }
        });

        const medResultsProcessed: Suggestion[] = (medResults ?? []).map(
          (med: any) => ({
            strength: med.strength,
            unit: med.unit,
            morning: med.morning ?? 0,
            afternoon: med.afternoon ?? 0,
            evening: 0, // kept as in original code
            night: med.evening ?? 0, // original mapped night = med.evening
            frequencyPerWeek: "Everyday",
            instruction: med.instruction,
            type: med.type,
            name: med.name,
            search_name: med.name ?? "",
            beforeFood: med.beforeFood === "Yes" || med.beforeFood === true,
            category: med.category,
          })
        );

        const mergedAndSortedResults = [
          ...medTempResultsProcessed,
          ...medResultsProcessed,
        ].sort((a, b) => {
          const nameA = (a.search_name ?? a.name ?? "").toLowerCase();
          const nameB = (b.search_name ?? b.name ?? "").toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });

        if (mounted) setMedSuggestions(mergedAndSortedResults);
      } catch (err) {
        console.error("Medicine autocomplete error:", err);
        if (mounted) setMedSuggestions([]);
      }
    })();

    return () => {
      mounted = false;
    };
    // run when the search text or searchTemplates flag changes
  }, [medicineName, searchTemplates]);

  return (
    <Autocomplete
      id="px-search-medicine-input"
      options={medSuggestions}
      getOptionLabel={(option: Suggestion) =>
        option.search_name ?? option.name ?? ""
      }
      filterOptions={(options) => options} // server-returned suggestions already filtered
      onChange={(_, newValue: Suggestion | null) => {
        onSelectHandler(newValue);
        setIsOpen(false);
      }}
      open={isOpen}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
      renderInput={(params) => (
        <div ref={params.InputProps?.ref}>
          <input
            {...(params.inputProps as any)}
            className={classes.textField}
            placeholder="Search medicine"
            value={medicineName}
            onClick={() => setIsOpen(true)}
            onChange={(e) => {
              setIsOpen(true);
              onNameChangeHandler(e.target.value);
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "8px 10px",
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
          />
        </div>
      )}
    />
  );
};

export default MedicineAutocomplete;
